import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Send, History, Users, Mail, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const AdminEmail = () => {
    const [tab, setTab] = useState("compose");
    const [compose, setCompose] = useState({ to: "", subject: "", body_html: "" });
    const [broadcast, setBroadcast] = useState({ audience: "customers", subject: "", body_html: "" });
    const [testTo, setTestTo] = useState("");
    const [logs, setLogs] = useState([]);
    const [sending, setSending] = useState(false);

    const loadLogs = async () => {
        try { const { data } = await api.get("/admin/email/logs"); setLogs(data.logs); } catch {}
    };
    useEffect(() => { loadLogs(); }, []);

    const send = async () => {
        const to_list = compose.to.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
        if (!to_list.length || !compose.subject || !compose.body_html) return toast.error("Fill all fields");
        setSending(true);
        try {
            const { data } = await api.post("/admin/email/send", { to: to_list, subject: compose.subject, body_html: compose.body_html });
            if (data.ok) toast.success("Email sent"); else toast.error(data.message || "Failed");
            setCompose({ to: "", subject: "", body_html: "" });
            loadLogs();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSending(false); }
    };

    const doBroadcast = async () => {
        if (!broadcast.subject || !broadcast.body_html) return toast.error("Subject & body required");
        if (!confirm(`Broadcast to ${broadcast.audience}?`)) return;
        setSending(true);
        try {
            const { data } = await api.post("/admin/email/broadcast", broadcast);
            if (data.ok) toast.success(`Sent to ${data.recipients} recipients`); else toast.error(data.message || "Failed");
            setBroadcast({ ...broadcast, subject: "", body_html: "" });
            loadLogs();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSending(false); }
    };

    const doTest = async () => {
        setSending(true);
        try {
            const { data } = await api.post("/admin/email/test", { to: testTo });
            if (data.ok) toast.success("Test email sent!"); else toast.error(data.message || "SMTP not configured");
            loadLogs();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSending(false); }
    };

    return (
        <div className="p-8" data-testid="admin-email">
            <div className="mb-6">
                <h1 className="font-display text-3xl font-bold tracking-tight">Email</h1>
                <p className="text-sm text-muted-foreground">Send transactional and marketing emails. Configure Gmail SMTP in <strong>Settings → Email</strong> first.</p>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="rounded-full bg-muted/60 p-1">
                    <TabsTrigger value="compose" data-testid="tab-compose"><Mail className="w-4 h-4 mr-1" />Compose</TabsTrigger>
                    <TabsTrigger value="broadcast" data-testid="tab-broadcast"><Radio className="w-4 h-4 mr-1" />Broadcast</TabsTrigger>
                    <TabsTrigger value="test" data-testid="tab-test">Test SMTP</TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-1" />History</TabsTrigger>
                </TabsList>

                <TabsContent value="compose" className="mt-4">
                    <div className="rounded-2xl bg-card border p-5 space-y-3 max-w-3xl">
                        <Input placeholder="To (comma or space separated)" value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} data-testid="email-to" className="rounded-xl h-11" />
                        <Input placeholder="Subject" value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} data-testid="email-subject" className="rounded-xl h-11" />
                        <textarea placeholder="Message body (HTML supported)" value={compose.body_html} onChange={(e) => setCompose({ ...compose, body_html: e.target.value })} data-testid="email-body" className="w-full rounded-xl border p-3 text-sm min-h-[180px]" />
                        <Button onClick={send} disabled={sending} data-testid="email-send-btn" className="rounded-full bg-primary hover:bg-primary/90 h-11 px-6">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send email</>}
                        </Button>
                    </div>
                </TabsContent>

                <TabsContent value="broadcast" className="mt-4">
                    <div className="rounded-2xl bg-card border p-5 space-y-3 max-w-3xl">
                        <div className="text-[11px] px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900" data-testid="broadcast-bcc-note">
                            Recipients are added as <b>BCC</b> — nobody sees anyone else's address. Large lists are sent in batches of 50.
                        </div>
                        <label className="block text-sm">
                            <div className="text-xs text-muted-foreground mb-1">Audience</div>
                            <select value={broadcast.audience} onChange={(e) => setBroadcast({ ...broadcast, audience: e.target.value })} className="w-full rounded-xl border h-11 px-3 text-sm">
                                <option value="customers">All customers</option>
                                <option value="leads">All leads</option>
                                <option value="all">All (customers + leads)</option>
                            </select>
                        </label>
                        <Input placeholder="Subject" value={broadcast.subject} onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })} data-testid="broadcast-subject" className="rounded-xl h-11" />
                        <textarea placeholder="Message body (HTML supported)" value={broadcast.body_html} onChange={(e) => setBroadcast({ ...broadcast, body_html: e.target.value })} data-testid="broadcast-body" className="w-full rounded-xl border p-3 text-sm min-h-[180px]" />
                        <Button onClick={doBroadcast} disabled={sending} data-testid="broadcast-send-btn" className="rounded-full bg-primary hover:bg-primary/90 h-11 px-6">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Radio className="w-4 h-4" /> Broadcast</>}
                        </Button>
                    </div>
                </TabsContent>

                <TabsContent value="test" className="mt-4">
                    <div className="rounded-2xl bg-card border p-5 space-y-3 max-w-lg">
                        <p className="text-sm text-muted-foreground">Send a test email to verify your Gmail SMTP configuration.</p>
                        <Input placeholder="Send to (email)" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="email-test-to" className="rounded-xl h-11" />
                        <Button onClick={doTest} disabled={sending} data-testid="email-test-btn" className="rounded-full bg-primary hover:bg-primary/90 h-11 px-6">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send test email"}
                        </Button>
                    </div>
                </TabsContent>

                <TabsContent value="history" className="mt-4">
                    <div className="rounded-2xl bg-card border overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50 text-left">
                                <tr>
                                    <th className="p-3">When</th><th className="p-3">Subject</th><th className="p-3">To</th><th className="p-3">Kind</th><th className="p-3">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((l) => (
                                    <tr key={l.id} className="border-t border-border">
                                        <td className="p-3 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</td>
                                        <td className="p-3">{l.subject}</td>
                                        <td className="p-3 text-xs text-muted-foreground">{(l.to || []).slice(0, 2).join(", ")}{l.to?.length > 2 ? ` +${l.to.length - 2}` : ""}</td>
                                        <td className="p-3 text-xs">{l.kind}</td>
                                        <td className="p-3">
                                            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                                                l.status === "sent" ? "bg-emerald-100 text-emerald-700" :
                                                l.status === "failed" ? "bg-destructive/10 text-destructive" :
                                                "bg-muted text-muted-foreground"
                                            }`}>{l.status}</span>
                                        </td>
                                    </tr>
                                ))}
                                {logs.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">No emails sent yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default AdminEmail;
